import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import type { EditorJsData } from '@/types'

export interface NewsTag {
  id: number
  name: string
  slug: string
  color: string
}

/** Полный формат новости (админка) */
export interface News {
  id: number
  title: string
  slug: string
  preview: string
  body_json: EditorJsData | null
  cover_path: string | null
  cover_url: string | null
  og_image_path: string | null
  og_image_url: string | null
  is_published: boolean
  published_at: string | null
  is_pinned: boolean
  meta_title: string | null
  meta_description: string | null
  author_id: number | null
  tags?: NewsTag[]
  created_at: string | null
  updated_at: string | null
}

/** Компактный элемент админского списка (без body_json) */
export interface NewsListItem {
  id: number
  title: string
  slug: string
  preview: string
  cover_url: string | null
  is_published: boolean
  published_at: string | null
  is_pinned: boolean
  /** Число прочтений; приходит только в админском списке */
  reads_count?: number
  tags?: NewsTag[]
  created_at: string | null
  updated_at: string | null
}

/** Публичная новость лэндинга */
export interface PublicNews {
  title: string
  slug: string
  preview: string
  body_json?: EditorJsData | null
  cover_url: string | null
  is_pinned: boolean
  meta_title: string | null
  meta_description: string | null
  published_at: string | null
  tags?: NewsTag[]
}

/** Строка статистики прочтений (админ) */
export interface NewsReadRow {
  user_id: number
  read_at: string
  user: {
    id: number
    name: string
    email: string
    avatar_url: string | null
    roles: string[]
  } | null
}

export interface NewsListParams {
  search?: string
  is_published?: boolean
  tag_id?: number
  sort?: 'id' | 'title' | 'published_at' | 'created_at'
  order?: 'asc' | 'desc'
  page?: number
  per_page?: number
}

export interface NewsInput {
  title?: string
  slug?: string | null
  preview?: string | null
  body_json?: EditorJsData
  cover_path?: string | null
  og_image_path?: string | null
  is_pinned?: boolean
  meta_title?: string | null
  meta_description?: string | null
  tag_ids?: number[]
  publish?: boolean
}

export interface PaginationMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

interface PaginatedResponse<T> {
  data: T[]
  meta: PaginationMeta
}

interface ItemResponse<T> {
  message?: string
  data: T
}

export const newsApi = {
  // --- Админка ---

  getList: async (params?: NewsListParams): Promise<{ data: NewsListItem[]; meta: PaginationMeta }> => {
    const response = await apiClient.get<PaginatedResponse<NewsListItem>>(
      API_ENDPOINTS.ADMIN.NEWS.LIST,
      { params },
    )
    return { data: response.data.data, meta: response.data.meta }
  },

  getOne: async (id: number): Promise<News> => {
    const response = await apiClient.get<ItemResponse<News>>(API_ENDPOINTS.ADMIN.NEWS.ITEM(id))
    return response.data.data
  },

  create: async (body: NewsInput): Promise<News> => {
    const response = await apiClient.post<ItemResponse<News>>(API_ENDPOINTS.ADMIN.NEWS.LIST, body)
    return response.data.data
  },

  update: async (id: number, body: NewsInput): Promise<News> => {
    const response = await apiClient.patch<ItemResponse<News>>(
      API_ENDPOINTS.ADMIN.NEWS.ITEM(id),
      body,
    )
    return response.data.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.ADMIN.NEWS.ITEM(id))
  },

  publish: async (id: number): Promise<News> => {
    const response = await apiClient.post<ItemResponse<News>>(API_ENDPOINTS.ADMIN.NEWS.PUBLISH(id))
    return response.data.data
  },

  unpublish: async (id: number): Promise<News> => {
    const response = await apiClient.post<ItemResponse<News>>(
      API_ENDPOINTS.ADMIN.NEWS.UNPUBLISH(id),
    )
    return response.data.data
  },

  uploadCover: async (id: number, file: File): Promise<News> => {
    const form = new FormData()
    form.append('image', file)
    const response = await apiClient.post<ItemResponse<News>>(
      API_ENDPOINTS.ADMIN.NEWS.COVER(id),
      form,
    )
    return response.data.data
  },

  /** Загрузка изображения тела (формат ответа Editor.js image tool) */
  uploadImage: async (file: File): Promise<{ url: string; path: string }> => {
    const form = new FormData()
    form.append('image', file)
    const response = await apiClient.post<{ success: number; file: { url: string; path: string } }>(
      API_ENDPOINTS.ADMIN.NEWS.IMAGES,
      form,
    )
    return response.data.file
  },

  getPreviewLink: async (id: number): Promise<string> => {
    const response = await apiClient.get<ItemResponse<{ url: string }>>(
      API_ENDPOINTS.ADMIN.NEWS.PREVIEW_LINK(id),
    )
    return response.data.data.url
  },

  // --- Теги ---

  getTags: async (): Promise<NewsTag[]> => {
    const response = await apiClient.get<ItemResponse<NewsTag[]>>(API_ENDPOINTS.ADMIN.NEWS.TAGS)
    return response.data.data
  },

  createTag: async (body: { name: string; color?: string }): Promise<NewsTag> => {
    const response = await apiClient.post<ItemResponse<NewsTag>>(
      API_ENDPOINTS.ADMIN.NEWS.TAGS,
      body,
    )
    return response.data.data
  },

  updateTag: async (id: number, body: { name?: string; color?: string }): Promise<NewsTag> => {
    const response = await apiClient.patch<ItemResponse<NewsTag>>(
      API_ENDPOINTS.ADMIN.NEWS.TAG(id),
      body,
    )
    return response.data.data
  },

  deleteTag: async (id: number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.ADMIN.NEWS.TAG(id))
  },

  // --- Публичный лэндинг (без авторизации) ---

  getPublicList: async (perPage = 6): Promise<PublicNews[]> => {
    const response = await apiClient.get<PaginatedResponse<PublicNews>>(
      API_ENDPOINTS.LANDING.NEWS,
      { params: { per_page: perPage } },
    )
    return response.data.data
  },

  /** Страница списка: с метой для пагинации */
  getPublicPage: async (
    page: number,
    perPage: number,
  ): Promise<{ data: PublicNews[]; meta: PaginationMeta }> => {
    const response = await apiClient.get<PaginatedResponse<PublicNews>>(
      API_ENDPOINTS.LANDING.NEWS,
      { params: { page, per_page: perPage } },
    )
    return { data: response.data.data, meta: response.data.meta }
  },

  /** Деталь по slug; queryString — подписанные параметры preview-ссылки (expires/signature) */
  getPublicOne: async (slug: string, queryString?: string): Promise<PublicNews> => {
    const url = API_ENDPOINTS.LANDING.NEWS_ITEM(slug) + (queryString ? `?${queryString}` : '')
    const response = await apiClient.get<ItemResponse<PublicNews>>(url)
    return response.data.data
  },

  /** Отметить прочитанной (только для авторизованных; ошибки не критичны) */
  markRead: async (slug: string): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.LANDING.NEWS_READ(slug))
  },

  // --- Статистика прочтений (админ) ---

  getReads: async (
    id: number,
    params: { page?: number; per_page?: number },
  ): Promise<{ data: NewsReadRow[]; meta: PaginationMeta }> => {
    const response = await apiClient.get<PaginatedResponse<NewsReadRow>>(
      API_ENDPOINTS.ADMIN.NEWS.READS(id),
      { params },
    )
    return { data: response.data.data, meta: response.data.meta }
  },
}
